// @flow

import type { Endpoints, EntityFieldCollection, SortParams } from '../types';
import { createdAtField, getCreatedAtSort, idField, paymentModeField } from '../abstract/StoredEntity';
import type { StoredEntity } from '../abstract/StoredEntity';
import { constructEndpoints } from '../helpers';
import Organization from './Organization';
import User from './User';

class RewardAccount implements StoredEntity {
    static classInternalName = 'reward_account';

    static fields: EntityFieldCollection = {
        id: idField,
        createdAt: createdAtField,
        organizationId: {
            displayName: 'Organization',
            recordName: 'organization_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => Organization,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: true,
            canEdit: false,
            isOptional: false,
            displayOnMain: true,
        },
        userId: {
            displayName: 'User',
            recordName: 'user_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => User,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: true,
            canEdit: false,
            isOptional: false,
            displayOnMain: true,
        },
        totalEarned: {
            displayName: 'Total Earned',
            recordName: 'total_earned',
            dataType: {
                primitive: 'num',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: false,
            isOptional: false,
            displayOnMain: true,
        },
        balance: {
            displayName: 'Balance',
            recordName: 'balance',
            dataType: {
                primitive: 'num',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: false,
            isOptional: false,
            displayOnMain: true,
        },
        pendingAmount: {
            displayName: 'Pending Amount',
            recordName: 'pending_amount',
            dataType: {
                primitive: 'num',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: false,
            isOptional: false,
            displayOnMain: true,
        },
        enabled: {
            displayName: 'Enabled',
            recordName: 'enabled',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        paymentMode: paymentModeField,
    };

    static rootEndpoint = 'reward-accounts';

    static endpoints: Endpoints = constructEndpoints(RewardAccount.rootEndpoint);

    static fileFields: EntityFieldCollection = {};

    static defaultSort: SortParams = getCreatedAtSort('created_at');

    get id() {
        return this.record[RewardAccount.fields.id.recordName];
    }

    get displayName() {
        return this.record[RewardAccount.fields.id.recordName];
    }

    constructor(record: any) {
        this.record = record;
    }
}

export default RewardAccount;
